import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import { useSelector } from "react-redux"
import axios from "axios"
import Sidebar from "../components/Sidebar"
import DashboardHeader from "../components/DashboardHeader"

const BASE_URL = process.env.REACT_APP_API_BASE_URL

const QuestionDetailPage = () => {
  const { id } = useParams()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [question, setQuestion] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [markingHelpful, setMarkingHelpful] = useState(false)
  const [profilePhoto, setProfilePhoto] = useState(null);
  
  const user = useSelector((state) => state.auth.user);

  const userData = {
    name: "Alyxcyz May Servino",
    initials: "AM",
    notificationCount: 4,
  }

  // Fetch profile photo separately
    useEffect(() => {
      const fetchUserProfile = async () => {
        try {
          const token = localStorage.getItem("access");
          const res = await axios.get(`${BASE_URL}/users/profile/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const p = res.data.profile_photo;
          if (p) {
            setProfilePhoto(
              p.startsWith("http") ? p : `${BASE_URL}${p}`
            );
          } else {
            setProfilePhoto(null);
          }
        } catch (e) {
          setProfilePhoto(null);
        }
      };
      fetchUserProfile();
    }, []);

  useEffect(() => {
    fetchQuestionDetail()
  }, [id])

  const fetchQuestionDetail = async () => {
    setLoading(true)
    setError(null)

    try {
      // Get token from localStorage
      const token = localStorage.getItem("access")

      // Make API call with authorization header
      const response = await axios.get(`${BASE_URL}/users/questions/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      setQuestion(response.data)
    } catch (err) {
      setError("Failed to load question details. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  const handleMarkHelpful = async () => {
    if (markingHelpful) return

    setMarkingHelpful(true)
    try {
      const token = localStorage.getItem("access")

      // Make API call to mark answer as helpful
      await axios.post(
        `${BASE_URL}/users/questions/${id}/helpful/`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )

      // Update local state to reflect the change
      setQuestion((prev) => ({
        ...prev,
        is_helpful: true,
        helpful_count: (prev.helpful_count || 0) + 1,
      }))

      // Could add a success notification here
    } catch (err) {
    } finally {
      setMarkingHelpful(false)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="d-flex">
          <Sidebar collapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed((prev) => !prev)}/>
          <div className="main-content flex-grow-1">
            <DashboardHeader user={user} profilePhoto={profilePhoto} onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
            <div className="dashboard-content p-4">
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden"></span>
                </div>
                <p className="mt-3 text-muted">Loading question details...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="d-flex">
          <Sidebar collapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed((prev) => !prev)}/>
          <div className="main-content flex-grow-1">
            <DashboardHeader user={user} onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
            <div className="dashboard-content p-4">
              <div className="alert alert-danger" role="alert">
                <i className="fas fa-exclamation-circle me-2"></i>
                {error}
                <div className="mt-3">
                  <button className="btn btn-outline-danger me-2" onClick={fetchQuestionDetail}>
                    <i className="fas fa-sync-alt me-1"></i> Retry
                  </button>
                  <Link to="/questions" className="btn btn-outline-secondary">
                    <i className="fas fa-arrow-left me-1"></i> Back to Questions
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!question) {
    return (
      <div className="dashboard-container">
        <div className="d-flex">
          <Sidebar collapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed((prev) => !prev)}/>
          <div className="main-content flex-grow-1">
            <DashboardHeader user={user} onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
            <div className="dashboard-content p-4">
              <div className="text-center py-5">
                <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                <h5 className="text-muted">Question not found</h5>
                <p className="text-muted">The question you're looking for doesn't exist.</p>
                <Link to="/questions" className="btn btn-primary">
                  <i className="fas fa-arrow-left me-2"></i>
                  Back to Questions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="dashboard-container">
      <div className="d-flex">
        <Sidebar collapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed((prev) => !prev)}/>
        <div className="main-content flex-grow-1">
          <DashboardHeader user={user} onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
          <div className="dashboard-content p-4">
            {/* Back Button */}
            <div className="mb-3">
              <Link to="/questions" className="btn btn-link text-warning p-0 text-decoration-none">
                <i className="fas fa-arrow-left me-2"></i>
                Back to Questions
              </Link>
            </div>

            {/* Question Detail */}
            <div className="question-detail">
              <div className="question-header mb-4">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="question-meta mb-2">
                      <span className="badge bg-secondary me-2">{question.category}</span>
                      <span className={`badge ${question.status === "answered" ? "bg-success" : "bg-warning"}`}>
                        {question.status === "answered" ? "Answered" : "Pending"}
                      </span>
                    </div>
                    <h2 className="question-title">Q: {question.question_text}</h2>
                    <div className="question-info">
                      <small className="text-muted">
                        <i className="fas fa-calendar me-1"></i>
                        Asked on {formatDate(question.created_at)}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Question Card */}
              <div className="question-card mb-4">
                <div className="card">
                  <div className="card-header bg-light">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-question-circle text-primary me-2"></i>
                      Your Question
                    </h5>
                  </div>
                  <div className="card-body">
                    <p className="card-text">{question.question_text}</p>
                  </div>
                </div>
              </div>

              {/* Answer Card */}
              <div className="answer-card">
                <div className="card">
                  <div className="card-header bg-light">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-reply text-success me-2"></i>
                      Answer
                    </h5>
                  </div>
                  <div className="card-body">
                    {question.answer_text ? (
                      <>
                        <div className="answer-content mb-3">
                          <p className="card-text">{question.answer_text}</p>
                        </div>
                        {question.answered_by && (
                          <div className="answer-attribution">
                            <hr />
                            <div className="d-flex align-items-center">
                              <div className="instructor-avatar me-3">
                                <i className="fas fa-user-tie"></i>
                              </div>
                              <div>
                                <div className="instructor-name fw-bold">{question.answered_by}</div>
                                <div className="instructor-title text-muted">{question.instructor_title}</div>
                                <small className="text-muted">
                                  <i className="fas fa-clock me-1"></i>
                                  Answered on {formatDate(question.answered_at)}
                                </small>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="no-answer">
                        <div className="text-center py-4">
                          <i className="fas fa-hourglass-half fa-2x text-warning mb-3"></i>
                          <h6 className="text-muted">No answer yet</h6>
                          <p className="text-muted mb-0">
                            Your question is being reviewed by our instructors. Please check back later.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="question-actions mt-4">
                <div className="d-flex gap-2">
                  <Link to="/questions" className="btn btn-outline-secondary">
                    <i className="fas fa-list me-2"></i>
                    All Questions
                  </Link>
                  {/*{question.status === "answered" && (
                    <button
                      className={`btn ${question.is_helpful ? "btn-success" : "btn-outline-success"}`}
                      onClick={handleMarkHelpful}
                      disabled={markingHelpful || question.is_helpful}
                    >
                      <i className={`fas fa-thumbs-up me-2 ${markingHelpful ? "fa-spin" : ""}`}></i>
                      {question.is_helpful ? "Marked Helpful" : "Mark as Helpful"}
                      {question.helpful_count > 0 && (
                        <span className="ms-2 badge bg-light text-dark">{question.helpful_count}</span>
                      )}
                    </button>
                  )}*/}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuestionDetailPage
